// Shared metadata + icon resolver for data-source connectors.
//
// Both `ConnectorsPage` and `DataRelationshipsPage` need to render a brand
// icon for a connector type — keeping the SVG imports and label map in one
// place avoids the two pages drifting apart over time.

import React from 'react';
import { ReactComponent as GoogleDriveSvg } from '../assets/connectors/google-drive.svg';
import { ReactComponent as PostgresSvg } from '../assets/connectors/postgres.svg';
import { ReactComponent as MySQLSvg } from '../assets/connectors/mysql.svg';
import { ReactComponent as OneDriveSvg } from '../assets/connectors/onedrive.svg';
import { ReactComponent as SharePointSvg } from '../assets/connectors/sharepoint.svg';
import { ReactComponent as GoogleAdsSvg } from '../assets/connectors/google-ads.svg';
import { ReactComponent as BigQuerySvg } from '../assets/connectors/bigquery.svg';
import { ReactComponent as SnowflakeSvg } from '../assets/connectors/Snowflake.svg';
import { ReactComponent as RedshiftSvg } from '../assets/connectors/redshift.svg';
import { ReactComponent as ShopifySvg } from '../assets/connectors/shopify.svg';
import { ReactComponent as HubSpotSvg } from '../assets/connectors/hubspot.svg';
import { ReactComponent as AgencyZoomSvg } from '../assets/connectors/agencyzoom.svg';
import { ReactComponent as MCPSvg } from '../assets/connectors/mcp.svg';
import { ReactComponent as AirtableSvg } from '../assets/connectors/airtable.svg';

export const CONNECTOR_TYPE_LABELS: Record<string, string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  snowflake: 'Snowflake',
  redshift: 'AWS Redshift',
  bigquery: 'BigQuery',
  google_drive: 'Google Drive',
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  shopify: 'Shopify',
  hubspot: 'HubSpot CRM',
  agencyzoom: 'AgencyZoom',
  airtable: 'Airtable',
  google_ads: 'Google Ads',
  mcp: 'MCP',
};

export function prettyConnectorType(t: string): string {
  if (!t) return '';
  return CONNECTOR_TYPE_LABELS[t] || t.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export const CONNECTOR_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  postgres: PostgresSvg,
  mysql: MySQLSvg,
  google_drive: GoogleDriveSvg,
  snowflake: SnowflakeSvg,
  redshift: RedshiftSvg,
  onedrive: OneDriveSvg,
  sharepoint: SharePointSvg,
  google_ads: GoogleAdsSvg,
  bigquery: BigQuerySvg,
  shopify: ShopifySvg,
  hubspot: HubSpotSvg,
  agencyzoom: AgencyZoomSvg,
  mcp: MCPSvg,
  airtable: AirtableSvg,
};

// Render a connector icon at the given pixel size. Falls back to `null`
// when the type is unknown — callers should provide their own fallback
// (e.g. a generic Storage icon) so this stays purely about brand glyphs.
export function ConnectorIcon({ type, size = 16 }: { type?: string; size?: number }): React.ReactElement | null {
  if (!type) return null;
  const Icon = CONNECTOR_ICONS[type];
  if (!Icon) return null;
  return <Icon width={size} height={size} />;
}

// Connector types whose source items are files (CSV / XLSX / etc.), not tables.
const FILE_CONNECTOR_TYPES = new Set(['google_drive', 'onedrive', 'sharepoint', 'dropbox']);

export function isFileConnectorType(type: string): boolean {
  return FILE_CONNECTOR_TYPES.has(type);
}

/**
 * Pick the right noun for a count of source items mapped to a canonical entity.
 * Uses "files" / "tables" when all sources share one category, and falls back
 * to the generic "sources" for mixed connector types (e.g. Postgres + Drive).
 */
export function sourceItemNoun(types: string[], count: number): string {
  if (types.length === 0) return count === 1 ? 'source' : 'sources';
  const allFiles = types.every(isFileConnectorType);
  const allNonFiles = types.every((t) => !isFileConnectorType(t));
  if (allFiles) return count === 1 ? 'file' : 'files';
  if (allNonFiles) return count === 1 ? 'table' : 'tables';
  return count === 1 ? 'source' : 'sources';
}
